import React from "react";
import { Day, Habit, ReportPeriod } from "../types";
import { colors } from "../colors";
import { generatePeriodDays } from "../utils/periodUtils";

interface BarChartProps {
  days: Day[];
  habits: Habit[];
  period?: ReportPeriod;
}

export const BarChart: React.FC<BarChartProps> = ({ days, habits, period = "month" }) => {
  const periodDays = generatePeriodDays(period);

  const dayScores = periodDays.map(date => {
    const day = days.find(d => d.date === date);

    if (!day || !day.habits.length) {
      return { date, totalPositive: 0, totalNegative: 0 };
    }

    const habitScores = day.habits.map(habitId => {
      const habit = habits.find(h => h.id === habitId);
      return habit ? habit.score : 0;
    });

    const totalPositive = habitScores.filter(score => score > 0).reduce((sum, score) => sum + score, 0);
    const totalNegative = habitScores.filter(score => score < 0).reduce((sum, score) => sum + score, 0);

    return { date, totalPositive, totalNegative };
  });

  const maxPositiveValue = Math.max(...dayScores.map(day => day.totalPositive));
  const minNegativeValue = Math.min(...dayScores.map(day => day.totalNegative));

  const maxBarHeight = 100;
  const barWidth = `calc(100% / ${periodDays.length})`;

  return (
    <div style={{
      display: 'flex',
      height: '150px',
      width: '100%',
      alignItems: 'center',
      position: 'relative',
      marginBottom: '20px'
    }}>
      {dayScores.map((day, index) => (
        <div key={index} style={{
          width: barWidth,
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          position: 'relative'
        }}>
          {day.totalPositive > 0 && (
            <div style={{
              position: 'absolute',
              bottom: '50%',
              width: '60%',
              height: `${(day.totalPositive / maxPositiveValue) * (maxBarHeight / 2)}px`,
              backgroundColor: colors.green.medium,
              borderRadius: '3px 3px 0 0'
            }} />
          )}

          {day.totalNegative < 0 && (
            <div style={{
              position: 'absolute',
              top: '50%',
              width: '60%',
              height: `${(Math.abs(day.totalNegative) / Math.abs(minNegativeValue)) * (maxBarHeight / 2)}px`,
              backgroundColor: colors.red.medium,
              borderRadius: '0 0 3px 3px'
            }} />
          )}

          <div style={{
            position: 'absolute',
            width: '100%',
            height: '1px',
            backgroundColor: '#ddd',
            top: '50%'
          }} />
        </div>
      ))}
    </div>
  );
};